import { StyleSheet, Platform, Share, View } from "react-native";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";
import { MemorySelector } from "@/components/memory/MemorySelector";
import { GroupSelector } from "@/components/groups/GroupSelector";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyContacts, deleteContact, inviteContact } from "@/services/firebase/user-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyGroups, addContactToGroup } from "@/services/firebase/group-functions";
import { ContactModel, ShareModel, GroupModel, MemoryModel } from "common247";
import { ContactCard } from "@components/contacts/ContactCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterContacts, computeFilteredCounts } from "@/services/unified-filter-service";

import { useAppDispatch, useAppSelector } from "@/services/store/hooks";
import {
  setContacts,
  updateContact,
  removeContact,
  setContactShares,
  setContactsLoading,
} from "@/services/store/features/contactsSlice";
import { invalidateShares, removeShare } from "@/services/store/features/sharesSlice";
import { entitySync } from "@/services/sync/entity-sync";
import { downloadMediaFile } from "@/utils/media-download";

const CONTACT_SORT_OPTIONS: ISortOption<ContactModel>[] = [
  { key: "displayName", label: "Name (A-Z)", getValue: (c) => c.displayName },
  { key: "email", label: "Email Address", getValue: (c) => c.email || "" },
  { key: "role", label: "User Role", getValue: (c) => c.userRole || "" },
  { key: "owner", label: "Owner", getValue: (c) => c.ownerDisplayName || "" },
];

const CONTACT_GROUP_OPTIONS: IGroupOption<ContactModel>[] = [
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (c) => c.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "role",
    label: "User Role",
    getGroupValue: (c) => (c.userRole === "owner" ? "My Contacts (Owner)" : `Shared with me (${c.userRole || "Viewer"})`),
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (c) => {
      const tags = Object.values(c.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

export default function MyContactsScreen() {
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();
  const dispatch = useAppDispatch();

  // In-memory Redux store state
  const { contacts, allContacts, sharesByContactId, lastFetched } = useAppSelector(
    (state) => state.contacts
  );

  const [loading, setLoading] = useState(contacts.length === 0 && !lastFetched);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("contacts");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isMemorySelectorOpen, setIsMemorySelectorOpen] = useState(false);
  const [isGroupSelectorOpen, setIsGroupSelectorOpen] = useState(false);

  // Sharing state
  const [sharingContact, setSharingContact] = useState<ContactModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const effectiveContacts = useMemo(() => {
    return filterContacts(contacts, filter);
  }, [contacts, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ contacts }, filter, "contacts");
  }, [contacts, filter]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const fetchContacts = useCallback(
    async (showLoading = false) => {
      if (!services) return;
      if (showLoading || contacts.length === 0) {
        setLoading(true);
      }
      dispatch(setContactsLoading(true));
      try {
        const response = await getMyContacts(services, { includeInactive: true, filter });
        if (response.status) {
          dispatch(
            setContacts({
              contacts: response.contacts || [],
              allContacts: response.contacts || [],
            })
          );
        } else {
          CustomAlert.alert("Error", response.error || "Failed to load contacts.");
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
        CustomAlert.alert("Error", "An unexpected error occurred while fetching contacts.");
      } finally {
        setLoading(false);
        dispatch(setContactsLoading(false));
      }
    },
    [services, dispatch, contacts.length, filter]
  );

  const fetchSharingData = useCallback(
    async (contactId: string) => {
      if (!services) return;
      setLoadingShares(true);
      try {
        const [sharesRes, groupsRes] = await Promise.all([
          getSharesForSource(services, { sourceId: contactId, sourceType: "contact" }),
          getMyGroups(services, {}),
        ]);
        if (sharesRes.status) {
          setShares(sharesRes.shares);
          dispatch(setContactShares({ contactId, shares: sharesRes.shares }));
        }
        if (groupsRes.status) setGroups(groupsRes.groups);
      } catch (error) {
        console.error("Error fetching sharing data:", error);
      } finally {
        setLoadingShares(false);
      }
    },
    [services, dispatch]
  );

  // Re-fetch in background on tab focus
  useFocusEffect(
    useCallback(() => {
      fetchContacts(false);
    }, [fetchContacts])
  );

  // Subscribe to real-time in-memory entity mutation events
  useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "share") {
        fetchContacts(false);
        if (sharingContact?.id) {
          fetchSharingData(sharingContact.id);
        }
      } else if (event.type === "contact") {
        if (event.action === "update" && event.data) {
          dispatch(updateContact(event.data));
        } else if (event.action === "delete") {
          dispatch(removeContact(event.id));
        } else {
          fetchContacts(false);
        }
      }
    });
    return unsubscribe;
  }, [fetchContacts, fetchSharingData, sharingContact?.id, dispatch]);

  const toggleItemSelection = (contact: ContactModel) => {
    if (!contact.id) return;
    const isSelected = selectedIds.includes(contact.id);
    const newSelected = isSelected
      ? selectedIds.filter((id) => id !== contact.id)
      : [...selectedIds, contact.id];
    setSelectedIds(newSelected);
    if (newSelected.length === 0) {
      setSelectionMode(false);
    }
  };

  const handleItemPress = (contact: ContactModel) => {
    if (isSelectionActive) {
      toggleItemSelection(contact);
    } else {
      router.push(`/my-contacts/${contact.id}`);
    }
  };

  const handleItemLongPress = (contact: ContactModel) => {
    if (!contact.id) return;
    setSelectionMode(true);
    if (!selectedIds.includes(contact.id)) {
      setSelectedIds((prev) => [...prev, contact.id!]);
    }
  };

  const handleItemContextMenu = (contact: ContactModel, _event?: any) => {
    handleItemLongPress(contact);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  const handleSelectAll = () => {
    setSelectedIds(contacts.map((c) => c.id!).filter(Boolean));
  };

  const getSelectedContactsList = () => {
    return contacts.filter((c) => selectedIds.includes(c.id!));
  };

  const handleBatchDownload = () => {
    const selected = getSelectedContactsList();
    if (selected.length === 0) return;
    const vcardEntries = selected.map(
      (c) =>
        `BEGIN:VCARD\nVERSION:3.0\nFN:${c.displayName || "Contact"}\nEMAIL:${c.email || ""}\nEND:VCARD`
    );
    const vcardData = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardEntries.join("\n\n"))}`;
    downloadMediaFile(vcardData, "contacts.vcf");
  };

  const handleBatchSendTo = async () => {
    const selected = getSelectedContactsList();
    if (selected.length === 0) return;
    const summary = selected
      .map((c) => `${c.displayName || "Contact"}: ${c.email || "No email"}`)
      .join("\n");
    try {
      if (Platform.OS !== "web") {
        await Share.share({ message: summary });
      } else if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title: "Contacts", text: summary });
      } else {
        await Share.share({ message: summary });
      }
    } catch (err) {
      console.error("Error sharing contacts:", err);
    }
  };

  const handleBatchDelete = () => {
    if (!services || selectedIds.length === 0) return;
    const deletableIds = selectedIds.filter((id) => {
      const c = contacts.find((item) => item.id === id);
      return !(c && user?.uid && c.userId === user.uid && c.ownerId === user.uid);
    });

    if (deletableIds.length === 0) {
      CustomAlert.alert("Cannot Delete", "The primary account contact cannot be deleted.");
      return;
    }

    CustomAlert.alert(
      "Delete Contacts",
      `Are you sure you want to delete ${deletableIds.length} selected contact(s)? This will remove them from all groups.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            let deletedCount = 0;
            for (const id of deletableIds) {
              try {
                const res = await deleteContact(services, { contactId: id });
                if (res.status) {
                  deletedCount++;
                  dispatch(removeContact(id));
                  entitySync.emit({ type: "contact", action: "delete", id });
                }
              } catch (err) {
                console.error("Failed to delete contact:", err);
              }
            }
            CustomAlert.alert("Deleted", `Deleted ${deletedCount} contact(s).`);
            handleClearSelection();
            fetchContacts();
          },
        },
      ]
    );
  };

  const handleBatchShareConfirm = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const res = await share(services, {
          sourceId: id,
          sourceType: "contact",
          targetId,
          targetType,
          role,
        });
        if (res.status) successCount++;
      } catch (err) {
        console.error("Failed to share contact:", err);
      }
    }
    CustomAlert.alert(
      "Sharing Complete",
      `Shared ${successCount} contact(s) with ${targetType}.`
    );
    handleClearSelection();
    fetchContacts();
  };

  const handleAddContactsToMemory = async (targetMemory: MemoryModel) => {
    if (!services || !targetMemory.id) return;
    let sharedCount = 0;
    for (const contactId of selectedIds) {
      try {
        const res = await share(services, {
          sourceId: targetMemory.id,
          sourceType: "memory",
          targetId: contactId,
          targetType: "contact",
          role: "viewer",
        });
        if (res.status) sharedCount++;
      } catch (err) {
        console.error("Error sharing memory with contact:", err);
      }
    }
    CustomAlert.alert(
      "Memory Shared",
      `Shared "${targetMemory.title}" with ${sharedCount} selected contact(s).`
    );
    handleClearSelection();
  };

  const handleAddContactsToGroup = async (targetGroup: GroupModel) => {
    const groupId = targetGroup.id || targetGroup.groupId;
    if (!services || !groupId) return;
    let successCount = 0;
    for (const contactId of selectedIds) {
      try {
        const res = await addContactToGroup(services, {
          groupId,
          contactId,
        });
        if (res.status) successCount++;
      } catch (err) {
        console.error("Error adding contact to group:", err);
      }
    }
    CustomAlert.alert(
      "Contacts Added",
      `Successfully added ${successCount} contact(s) to "${targetGroup.name}".`
    );
    handleClearSelection();
  };

  const batchActions: IBatchAction[] = [
    {
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: handleBatchDownload,
    },
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => setIsShareModalOpen(true),
    },
    {
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleBatchSendTo,
    },
    {
      id: "addToMemory",
      label: "Add to Memory",
      icon: "bookmark-outline",
      onPress: () => setIsMemorySelectorOpen(true),
      variant: "primary",
    },
    {
      id: "addToGroup",
      label: "Add to Group",
      icon: "people-outline",
      onPress: () => setIsGroupSelectorOpen(true),
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: handleBatchDelete,
      variant: "destructive",
    },
  ];

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !sharingContact) return;
    try {
      const res = await share(services, {
        sourceId: sharingContact.id!,
        sourceType: "contact",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchSharingData(sharingContact.id!);
        dispatch(invalidateShares());
        entitySync.emit({
          type: "share",
          action: "create",
          id: sharingContact.id!,
          data: { sourceId: sharingContact.id!, sourceType: "contact", targetId, targetType, role },
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingContact) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchSharingData(sharingContact.id!);
        dispatch(removeShare(shareId));
        dispatch(invalidateShares());
        entitySync.emit({
          type: "share",
          action: "delete",
          id: shareId,
          data: { sourceId: sharingContact.id!, sourceType: "contact" },
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share.");
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!services) return;
    const targetContact = contacts.find((c) => c.id === contactId);
    if (targetContact && user?.uid && targetContact.userId === user.uid && targetContact.ownerId === user.uid) {
      CustomAlert.alert("Cannot Delete", "The primary account contact cannot be deleted.");
      return;
    }
    await confirmEntityDeletion({
      services,
      entityType: "contact",
      entityId: contactId,
      entityName: targetContact?.displayName,
      onProceedDelete: (cascadeUnlink) => deleteContact(services, { contactId, cascadeUnlink }),
      onSuccess: () => {
        dispatch(removeContact(contactId));
        entitySync.emit({ type: "contact", action: "delete", id: contactId });
        fetchContacts();
      },
    });
  };

  const handleInviteContact = async (contactId: string) => {
    if (!services) return;
    CustomAlert.alert("Invite Contact", "Would you like to send this contact an invitation to join the platform?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Send Invite",
        onPress: async () => {
          setLoading(true);
          try {
            const res = await inviteContact(services, { contactId });
            if (res.status) {
              CustomAlert.alert("Success", res.message || "Invitation sent successfully.");
            } else {
              CustomAlert.alert("Error", res.error || "Failed to send invitation.");
            }
          } catch (err: any) {
            CustomAlert.alert("Error", err.message || "An error occurred.");
          } finally {
            setLoading(false);
          }
        },
      },
    ]);
  };

  const handleToggleInlineSharing = (item: ContactModel) => {
    if (sharingContact?.id === item.id) {
      setSharingContact(null);
    } else {
      setSharingContact(item);
      fetchSharingData(item.id!);
    }
  };

  return (
    <ThemedScreen title="Contacts" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="contacts"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allContacts={contacts}
          allGroups={groups}
        />
        <ThemedList<ContactModel>
          data={effectiveContacts}
          sortOptions={CONTACT_SORT_OPTIONS}
          defaultSortKey="displayName"
          defaultSortDirection="asc"
          groupOptions={CONTACT_GROUP_OPTIONS}
          persistenceKey="my-contacts"
          filterKeys={["displayName", "email", "userRole", "ownerDisplayName"]}
          filterPlaceholder="Search contacts..."
          loading={loading}
          onRefresh={fetchContacts}
          refreshing={loading}
          addButton={{
            title: isSelectionActive ? "Back" : "+ Add Contact",
            onPress: () => (isSelectionActive ? handleClearSelection() : router.push("/my-contacts/new")),
            variant: isSelectionActive ? "secondary" : "primary",
          }}
          onItemPress={handleItemPress}
          onItemLongPress={handleItemLongPress}
          onItemContextMenu={handleItemContextMenu}
          emptyText="No contacts found."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, isHovered, viewMode) => {
            const isMe = Boolean(user?.uid && item.userId === user.uid && item.ownerId === user.uid);
            const isExpanded = sharingContact?.id === item.id;
            const isSelected = selectedIds.includes(item.id!);

            return (
              <ContactCard
                contact={item}
                isMe={isMe}
                currentUserId={user?.uid}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => handleItemPress(item)}
                onLongPress={() => handleItemLongPress(item)}
                onContextMenu={(e) => handleItemContextMenu(item, e)}
                onEditPress={() => router.push(`/my-contacts/${item.id}`)}
                onDelete={!isMe ? () => handleDeleteContact(item.id!) : undefined}
                onInvite={() => handleInviteContact(item.id!)}
                isSharingExpanded={isExpanded}
                onToggleSharing={() => handleToggleInlineSharing(item)}
                shares={sharesByContactId[item.id!] ?? (isExpanded ? shares : undefined)}
                availableContacts={allContacts.filter((c) => c.id !== item.id && c.userId !== user?.uid)}
                availableGroups={groups}
                isLoadingShares={isExpanded && loadingShares}
                onShareTarget={handleShare}
                onUnshare={handleUnshare}
              />
            );
          }}
        />

        {/* Floating Contextual Batch Action Panel */}
        <ThemedActionHoverPanel
          visible={selectedIds.length > 0}
          selectedCount={selectedIds.length}
          totalCount={contacts.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={batchActions}
        />

        {/* Batch Share Modal */}
        <ShareRecipientSelector
          visible={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="contacts"
          onConfirmShare={handleBatchShareConfirm}
        />

        {/* Memory Selector Modal */}
        <MemorySelector
          visible={isMemorySelectorOpen}
          onClose={() => setIsMemorySelectorOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="contacts"
          onSelectMemory={handleAddContactsToMemory}
          onCreateNewMemory={() => router.push("/my-memories/new")}
        />

        {/* Group Selector Modal */}
        <GroupSelector
          visible={isGroupSelectorOpen}
          onClose={() => setIsGroupSelectorOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="contacts"
          onSelectGroup={handleAddContactsToGroup}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    paddingBottom: 0,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 40,
  },
});
